import React, { useEffect, useMemo, useState } from "react";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — Visual Charting Engine (Phase 27E).
 *
 * Original SVG candlestick chart engine. Renders:
 *   - Candlesticks (OHLC) with volume row
 *   - Two moving averages (SMA-20, SMA-50)
 *   - RSI subchart
 *   - MACD subchart
 *   - Support / Resistance band
 *   - Signal markers (entry / exit)
 *   - Entry / Stop-loss / Target lines
 *   - Risk : Reward box
 *
 * Deterministic mock OHLC generator until a real adapter is wired.
 */

const TIMEFRAMES = [
  { id: "1M",  label: "1m",  bars: 80 },
  { id: "2M",  label: "2m",  bars: 80 },
  { id: "5M",  label: "5m",  bars: 90 },
  { id: "10M", label: "10m", bars: 90 },
  { id: "15M", label: "15m", bars: 100 },
  { id: "1H",  label: "1H",  bars: 90 },
  { id: "1D",  label: "1D",  bars: 90 },
  { id: "1W",  label: "1W",  bars: 60 },
];

function hash(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = (h * 16777619) >>> 0; }
  return h;
}

function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeBars(symbol, timeframe) {
  const tfSpec = TIMEFRAMES.find((t) => t.id === timeframe) || TIMEFRAMES[6];
  const n = tfSpec.bars;
  const rng = mulberry32(hash(`${symbol}|${timeframe}`));
  const bars = [];
  let last = 100 + (hash(symbol || "AAPL") % 60);
  for (let i = 0; i < n; i++) {
    const drift = (rng() - 0.45) * 1.4;
    const range = 0.6 + rng() * 1.8;
    const open = last;
    const close = +(open + drift).toFixed(2);
    const high = +(Math.max(open, close) + rng() * range).toFixed(2);
    const low = +(Math.min(open, close) - rng() * range).toFixed(2);
    const volume = Math.round(1000 + rng() * 12000);
    bars.push({ i, open, high, low, close, volume });
    last = close;
  }
  return bars;
}

function sma(bars, period) {
  const out = new Array(bars.length).fill(null);
  let sum = 0;
  for (let i = 0; i < bars.length; i++) {
    sum += bars[i].close;
    if (i >= period) sum -= bars[i - period].close;
    if (i >= period - 1) out[i] = +(sum / period).toFixed(2);
  }
  return out;
}

function rsi(bars, period = 14) {
  const closes = bars.map((b) => b.close);
  const out = new Array(bars.length).fill(null);
  let gain = 0, loss = 0;
  for (let i = 1; i <= period && i < closes.length; i++) {
    const ch = closes[i] - closes[i - 1];
    if (ch >= 0) gain += ch; else loss -= ch;
  }
  let avgGain = gain / period, avgLoss = loss / period;
  out[period] = avgLoss === 0 ? 100 : 100 - (100 / (1 + avgGain / Math.max(avgLoss, 0.0001)));
  for (let i = period + 1; i < closes.length; i++) {
    const ch = closes[i] - closes[i - 1];
    const g = Math.max(0, ch), l = Math.max(0, -ch);
    avgGain = (avgGain * (period - 1) + g) / period;
    avgLoss = (avgLoss * (period - 1) + l) / period;
    out[i] = avgLoss === 0 ? 100 : 100 - (100 / (1 + avgGain / Math.max(avgLoss, 0.0001)));
  }
  return out.map((v) => v == null ? null : +v.toFixed(1));
}

function ema(values, period) {
  const k = 2 / (period + 1);
  const out = new Array(values.length).fill(null);
  let prev = null;
  for (let i = 0; i < values.length; i++) {
    if (values[i] == null) continue;
    prev = prev == null ? values[i] : values[i] * k + prev * (1 - k);
    out[i] = +prev.toFixed(3);
  }
  return out;
}

function macd(bars) {
  const closes = bars.map((b) => b.close);
  const ema12 = ema(closes, 12);
  const ema26 = ema(closes, 26);
  const macdLine = closes.map((_, i) => (ema12[i] != null && ema26[i] != null ? +(ema12[i] - ema26[i]).toFixed(3) : null));
  const signal = ema(macdLine.map((v) => v == null ? 0 : v), 9).map((v, i) => macdLine[i] == null ? null : v);
  const hist = macdLine.map((v, i) => (v != null && signal[i] != null ? +(v - signal[i]).toFixed(3) : null));
  return { macdLine, signal, hist };
}

function findSR(bars) {
  const highs = bars.map((b) => b.high);
  const lows = bars.map((b) => b.low);
  highs.sort((a, b) => b - a);
  lows.sort((a, b) => a - b);
  const top = highs.slice(0, Math.max(1, Math.floor(bars.length * 0.06)));
  const bot = lows.slice(0, Math.max(1, Math.floor(bars.length * 0.06)));
  const resistance = +(top.reduce((a, c) => a + c, 0) / top.length).toFixed(2);
  const support = +(bot.reduce((a, c) => a + c, 0) / bot.length).toFixed(2);
  return { support, resistance };
}

export default function OracleCandlestickChart({
  symbol = "AAPL",
  assetClass = "stocks",
  defaultTimeframe = "1H",
  height = 360,
  testid = "oracle-candlestick",
}) {
  const [tf, setTf] = useState(defaultTimeframe);
  const [showMa, setShowMa] = useState(true);
  const [showRsi, setShowRsi] = useState(true);
  const [showMacd, setShowMacd] = useState(true);

  const bars = useMemo(() => makeBars(symbol, tf), [symbol, tf]);
  const ma20 = useMemo(() => sma(bars, 20), [bars]);
  const ma50 = useMemo(() => sma(bars, 50), [bars]);
  const rsiVals = useMemo(() => rsi(bars), [bars]);
  const macdVals = useMemo(() => macd(bars), [bars]);
  const sr = useMemo(() => findSR(bars), [bars]);

  const last = bars[bars.length - 1];
  const entry = last?.close ?? 100;
  // Plan: SL 1.2 ATR below entry; Target 2 × stop (R:R = 2)
  const stop  = +(entry - Math.max(0.4, (sr.resistance - sr.support) * 0.18)).toFixed(2);
  const risk  = +(entry - stop).toFixed(2);
  const target = +(entry + risk * 2).toFixed(2);
  const rr = Math.max(0, risk) === 0 ? 0 : +(((target - entry) / Math.max(risk, 0.0001))).toFixed(2);

  // SVG layout
  const W = 800, H = height;
  const padL = 50, padR = 20, padT = 18, padB = 30;
  const rsiH = showRsi ? 56 : 0;
  const macdH = showMacd ? 64 : 0;
  const subPad = (rsiH ? 8 : 0) + (macdH ? 8 : 0);
  const mainH = H - padT - padB - rsiH - macdH - subPad;

  const allHighs = bars.map((b) => b.high);
  const allLows = bars.map((b) => b.low);
  const maxP = Math.max(...allHighs, target);
  const minP = Math.min(...allLows, stop);
  const xStep = (W - padL - padR) / Math.max(1, bars.length - 1);
  const yFor = (p) => padT + (mainH * (maxP - p)) / Math.max(0.0001, (maxP - minP));

  const candleW = Math.max(2, Math.floor(xStep * 0.7));

  // RSI / MACD geometry
  const rsiTop = padT + mainH + 12;
  const macdTop = rsiTop + rsiH + (rsiH ? 8 : 0);
  const rsiYFor = (v) => rsiTop + (rsiH * (100 - v)) / 100;
  const macdMin = Math.min(...macdVals.macdLine.filter((v) => v != null), ...macdVals.signal.filter((v) => v != null), 0);
  const macdMax = Math.max(...macdVals.macdLine.filter((v) => v != null), ...macdVals.signal.filter((v) => v != null), 0.01);
  const macdYFor = (v) => macdTop + (macdH * (macdMax - v)) / Math.max(0.0001, (macdMax - macdMin));

  const polyline = (vals) => vals
    .map((v, i) => (v == null ? "" : `${padL + i * xStep},${yFor(v)}`))
    .filter(Boolean).join(" ");
  const subPolyline = (vals, yFn) => vals
    .map((v, i) => (v == null ? "" : `${padL + i * xStep},${yFn(v)}`))
    .filter(Boolean).join(" ");

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div>
          <span style={S.tagline}>ORACLE Visual Charting Engine™</span>
          <h3 style={S.title}>{symbol} · <small style={{ color: "#94a3b8" }}>{assetClass}</small></h3>
        </div>
        <div style={S.controls} role="toolbar" aria-label="Chart controls">
          <div style={S.tfRow} role="tablist" aria-label="Timeframe">
            {TIMEFRAMES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTf(t.id)}
                role="tab"
                aria-selected={tf === t.id}
                data-testid={`chart-tf-${t.id}`}
                style={{ ...S.tfBtn, ...(tf === t.id ? S.tfBtnActive : {}) }}
              >{t.label}</button>
            ))}
          </div>
          <label style={S.toggle}><input type="checkbox" checked={showMa} onChange={(e) => setShowMa(e.target.checked)} data-testid="chart-toggle-ma" /> MA</label>
          <label style={S.toggle}><input type="checkbox" checked={showRsi} onChange={(e) => setShowRsi(e.target.checked)} data-testid="chart-toggle-rsi" /> RSI</label>
          <label style={S.toggle}><input type="checkbox" checked={showMacd} onChange={(e) => setShowMacd(e.target.checked)} data-testid="chart-toggle-macd" /> MACD</label>
        </div>
      </header>

      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", maxWidth: "100%", height: H, background: "#02060a", borderRadius: 10, border: "1px solid rgba(255,255,255,0.06)", display: "block" }} aria-label="ORACLE candlestick chart">
        {/* Y axis grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((p, i) => {
          const y = padT + p * mainH;
          return (
            <g key={`yg-${i}`}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="rgba(255,255,255,0.05)" />
              <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#64748b" fontFamily="monospace">
                {(maxP - (maxP - minP) * p).toFixed(2)}
              </text>
            </g>
          );
        })}
        {/* Support/Resistance band */}
        <rect x={padL} y={yFor(sr.resistance)} width={W - padL - padR} height={Math.max(2, yFor(sr.support) - yFor(sr.resistance))} fill="#c5a05914" stroke="#c5a05966" strokeDasharray="3 3" data-testid="chart-sr-band" />
        <text x={padL + 4} y={yFor(sr.resistance) + 9} fontSize="9" fill="#c5a059" fontFamily="monospace">RES {sr.resistance}</text>
        <text x={padL + 4} y={yFor(sr.support) - 3} fontSize="9" fill="#c5a059" fontFamily="monospace">SUP {sr.support}</text>
        {/* Risk:Reward box */}
        <rect x={W - padR - 60} y={yFor(target)} width={50} height={Math.max(2, yFor(entry) - yFor(target))} fill="#10b9811a" stroke="#10b981aa" data-testid="chart-rr-target-box" />
        <rect x={W - padR - 60} y={yFor(entry)} width={50} height={Math.max(2, yFor(stop) - yFor(entry))} fill="#ef44441a" stroke="#ef4444aa" data-testid="chart-rr-stop-box" />
        <text x={W - padR - 56} y={yFor(target) + 9} fontSize="9" fill="#10b981" fontFamily="monospace">TGT {target}</text>
        <text x={W - padR - 56} y={yFor(entry) - 3} fontSize="9" fill="#c5a059" fontFamily="monospace">ENT {entry}</text>
        <text x={W - padR - 56} y={yFor(stop) + 9} fontSize="9" fill="#ef4444" fontFamily="monospace">SL {stop}</text>
        <text x={W - padR - 60} y={yFor(target) - 4} fontSize="9" fill="#cbd5e1" fontFamily="monospace">R:R {rr}:1</text>
        {/* Entry / Stop / Target lines */}
        <line x1={padL} y1={yFor(entry)} x2={W - padR - 60} y2={yFor(entry)} stroke="#c5a059" strokeDasharray="4 4" data-testid="chart-entry-line" />
        <line x1={padL} y1={yFor(stop)} x2={W - padR - 60} y2={yFor(stop)} stroke="#ef4444" strokeDasharray="4 4" data-testid="chart-stop-line" />
        <line x1={padL} y1={yFor(target)} x2={W - padR - 60} y2={yFor(target)} stroke="#10b981" strokeDasharray="4 4" data-testid="chart-target-line" />
        {/* Candles */}
        {bars.map((b) => {
          const x = padL + b.i * xStep;
          const isUp = b.close >= b.open;
          const color = isUp ? "#10b981" : "#ef4444";
          const y1 = yFor(Math.max(b.open, b.close));
          const y2 = yFor(Math.min(b.open, b.close));
          const wickTop = yFor(b.high);
          const wickBot = yFor(b.low);
          return (
            <g key={`c-${b.i}`}>
              <line x1={x} y1={wickTop} x2={x} y2={wickBot} stroke={color} strokeWidth="1" />
              <rect x={x - candleW / 2} y={y1} width={candleW} height={Math.max(1, y2 - y1)} fill={color} opacity={isUp ? 0.85 : 0.85} />
            </g>
          );
        })}
        {/* MA20/MA50 */}
        {showMa && <polyline points={polyline(ma20)} fill="none" stroke="#c5a059" strokeWidth="1.4" data-testid="chart-ma20" />}
        {showMa && <polyline points={polyline(ma50)} fill="none" stroke="#67e8f9" strokeWidth="1.4" strokeDasharray="4 3" data-testid="chart-ma50" />}
        {/* Signal markers — entry above last bar */}
        <polygon points={`${padL + (bars.length - 1) * xStep - 5},${yFor(entry) + 12} ${padL + (bars.length - 1) * xStep + 5},${yFor(entry) + 12} ${padL + (bars.length - 1) * xStep},${yFor(entry) + 4}`} fill="#c5a059" data-testid="chart-entry-marker" />
        {/* RSI subchart */}
        {showRsi && (
          <g data-testid="chart-rsi-sub">
            <line x1={padL} y1={rsiYFor(70)} x2={W - padR} y2={rsiYFor(70)} stroke="#ef444466" strokeDasharray="3 3" />
            <line x1={padL} y1={rsiYFor(30)} x2={W - padR} y2={rsiYFor(30)} stroke="#10b98166" strokeDasharray="3 3" />
            <line x1={padL} y1={rsiYFor(50)} x2={W - padR} y2={rsiYFor(50)} stroke="rgba(255,255,255,0.05)" />
            <polyline points={subPolyline(rsiVals, rsiYFor)} fill="none" stroke="#a78bfa" strokeWidth="1.2" />
            <text x={padL - 6} y={rsiYFor(70) + 3} textAnchor="end" fontSize="9" fill="#64748b" fontFamily="monospace">RSI70</text>
            <text x={padL - 6} y={rsiYFor(30) + 3} textAnchor="end" fontSize="9" fill="#64748b" fontFamily="monospace">RSI30</text>
          </g>
        )}
        {/* MACD subchart */}
        {showMacd && (
          <g data-testid="chart-macd-sub">
            <line x1={padL} y1={macdYFor(0)} x2={W - padR} y2={macdYFor(0)} stroke="rgba(255,255,255,0.10)" />
            {macdVals.hist.map((h, i) => h == null ? null : (
              <line key={`mh-${i}`} x1={padL + i * xStep} y1={macdYFor(0)} x2={padL + i * xStep} y2={macdYFor(h)} stroke={h >= 0 ? "#10b98180" : "#ef444480"} strokeWidth="1.3" />
            ))}
            <polyline points={subPolyline(macdVals.macdLine, macdYFor)} fill="none" stroke="#fcd34d" strokeWidth="1.2" />
            <polyline points={subPolyline(macdVals.signal, macdYFor)} fill="none" stroke="#67e8f9" strokeWidth="1.2" />
            <text x={padL - 6} y={macdTop + 9} textAnchor="end" fontSize="9" fill="#64748b" fontFamily="monospace">MACD</text>
          </g>
        )}
      </svg>
      <ComplianceLock variant="inline" short policy="v27" testid={`${testid}-compliance`} />
    </section>
  );
}

const S = {
  shell: { background: "rgba(11,15,20,0.94)", border: "1px solid rgba(197,160,89,0.22)", borderRadius: 14, padding: 12, color: "#e2e8f0", display: "flex", flexDirection: "column", gap: 8, fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  tagline: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: "#fde68a", margin: 0, fontSize: 16 },
  controls: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  tfRow: { display: "flex", gap: 4, flexWrap: "wrap" },
  tfBtn: { appearance: "none", background: "transparent", border: "1px solid rgba(255,255,255,0.10)", color: "#94a3b8", padding: "3px 8px", borderRadius: 6, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.04em" },
  tfBtnActive: { background: "rgba(197,160,89,0.15)", borderColor: "#c5a059", color: "#c5a059" },
  toggle: { display: "inline-flex", alignItems: "center", gap: 4, color: "#cbd5e1", fontFamily: "'Space Mono', monospace", fontSize: 10.5, cursor: "pointer" },
};
